import { useEffect, useState, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Search, Filter, Eye, ChevronLeft, ChevronRight, Star, Users, 
  FileText, Image, AlertCircle, CheckCircle, ExternalLink, 
  GraduationCap, MapPin, CreditCard, Bell
} from 'lucide-react';
import { getTeachers, updateTeacherStatus, verifyTeacher } from '../lib/api';
import { toast } from 'sonner';

const ITEMS_PER_PAGE = 10;

export default function Teachers() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchTeachers();
  }, []);

  const fetchTeachers = async () => {
    try {
      const response = await getTeachers();
      setTeachers(response.data);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      toast.error('Failed to load teachers');
    } finally {
      setLoading(false);
    }
  };

  // Get unique subjects for filter
  const allSubjects = useMemo(() => {
    const subjects = new Set();
    teachers.forEach(t => t.subjects?.forEach(s => subjects.add(s)));
    return Array.from(subjects).sort();
  }, [teachers]);

  // Count unverified teachers
  const unverifiedCount = useMemo(() => {
    return teachers.filter(t => !t.isVerified).length;
  }, [teachers]);

  // Filter and search
  const filteredTeachers = useMemo(() => {
    return teachers.filter(teacher => {
      const matchesSearch = searchQuery === '' || 
        `${teacher.firstName} ${teacher.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
        teacher.email?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'active' && teacher.isActive) ||
        (statusFilter === 'inactive' && !teacher.isActive) ||
        (statusFilter === 'verified' && teacher.isVerified) ||
        (statusFilter === 'unverified' && !teacher.isVerified);
      
      const matchesSubject = subjectFilter === 'all' || 
        teacher.subjects?.includes(subjectFilter);
      
      return matchesSearch && matchesStatus && matchesSubject;
    });
  }, [teachers, searchQuery, statusFilter, subjectFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredTeachers.length / ITEMS_PER_PAGE);
  const paginatedTeachers = filteredTeachers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, subjectFilter]);

  const handleToggleStatus = async (teacherId, currentStatus) => {
    setActionLoading(teacherId);
    try {
      await updateTeacherStatus(teacherId, !currentStatus);
      setTeachers(prev => 
        prev.map(t => t.id === teacherId ? { ...t, isActive: !currentStatus } : t)
      );
      toast.success(`Teacher ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      toast.error('Failed to update teacher status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleVerify = async (teacherId, currentVerified) => {
    setActionLoading(teacherId);
    try {
      await verifyTeacher(teacherId, !currentVerified);
      setTeachers(prev => 
        prev.map(t => t.id === teacherId ? { ...t, isVerified: !currentVerified } : t)
      );
      if (selectedTeacher?.id === teacherId) {
        setSelectedTeacher(prev => ({ ...prev, isVerified: !currentVerified }));
      }
      toast.success(`Teacher ${!currentVerified ? 'verified' : 'unverified'} successfully`);
    } catch (error) {
      toast.error('Failed to update verification status');
    } finally {
      setActionLoading(null);
    }
  };

  // Document preview component
  const DocumentPreview = ({ url, label, icon: Icon }) => {
    if (!url) {
      return (
        <div className="flex items-center gap-2 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg text-slate-500">
          <Icon className="w-5 h-5" />
          <span className="text-sm">{label}: Not uploaded</span>
        </div>
      );
    }
    return (
      <a 
        href={url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="flex items-center gap-2 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors"
      >
        <Icon className="w-5 h-5" />
        <span className="text-sm font-medium flex-1">{label}</span>
        <ExternalLink className="w-4 h-4" />
      </a>
    );
  };

  return (
    <div data-testid="teachers-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
            Teachers
          </h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            Manage all registered teachers on the platform
          </p>
        </div>
        <div className="flex items-center gap-4">
          {unverifiedCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <Bell className="w-4 h-4 text-amber-600 dark:text-amber-400 animate-pulse" />
              <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
                {unverifiedCount} pending verification
              </span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-slate-400" />
            <span className="text-sm text-slate-500 dark:text-slate-400">
              {filteredTeachers.length} teachers
            </span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="teacher-search-input"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="teacher-status-filter" className="w-full sm:w-40">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="unverified">Unverified</SelectItem>
              </SelectContent>
            </Select>
            <Select value={subjectFilter} onValueChange={setSubjectFilter}>
              <SelectTrigger data-testid="teacher-subject-filter" className="w-full sm:w-48">
                <SelectValue placeholder="Subject" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subjects</SelectItem>
                {allSubjects.map(subject => (
                  <SelectItem key={subject} value={subject}>{subject}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                <TableHead className="font-semibold">Name</TableHead>
                <TableHead className="font-semibold">Email</TableHead>
                <TableHead className="font-semibold">Phone</TableHead>
                <TableHead className="font-semibold">Subjects</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Active</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-40" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-28" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-12" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                  </TableRow>
                ))
              ) : paginatedTeachers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-32 text-center text-slate-500">
                    No teachers found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedTeachers.map((teacher) => (
                  <TableRow 
                    key={teacher.id} 
                    data-testid={`teacher-row-${teacher.id}`}
                    className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors ${!teacher.isVerified ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {teacher.profilePhoto ? (
                          <img 
                            src={teacher.profilePhoto} 
                            alt={`${teacher.firstName} ${teacher.lastName}`}
                            className="w-9 h-9 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400 font-medium">
                            {teacher.firstName?.[0]}{teacher.lastName?.[0]}
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-slate-900 dark:text-white flex items-center gap-2">
                            {teacher.title} {teacher.firstName} {teacher.lastName}
                            {!teacher.isVerified && (
                              <AlertCircle className="w-4 h-4 text-amber-500" />
                            )}
                          </div>
                          {teacher.professionalTitle && (
                            <div className="text-xs text-slate-500">{teacher.professionalTitle}</div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600 dark:text-slate-400">{teacher.email}</TableCell>
                    <TableCell className="text-slate-600 dark:text-slate-400">{teacher.phone || '-'}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {teacher.subjects?.slice(0, 2).map((s, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {s}
                          </Badge>
                        ))}
                        {teacher.subjects?.length > 2 && (
                          <Badge variant="outline" className="text-xs">
                            +{teacher.subjects.length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {teacher.isVerified ? (
                        <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100 hover:bg-green-200">
                          <CheckCircle className="w-3 h-3 mr-1" /> Verified
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100 hover:bg-amber-200">
                          <AlertCircle className="w-3 h-3 mr-1" /> Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        data-testid={`teacher-toggle-${teacher.id}`}
                        checked={teacher.isActive}
                        disabled={actionLoading === teacher.id}
                        onCheckedChange={() => handleToggleStatus(teacher.id, teacher.isActive)}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        data-testid={`teacher-view-${teacher.id}`}
                        variant={teacher.isVerified ? "ghost" : "default"}
                        size="sm"
                        onClick={() => setSelectedTeacher(teacher)}
                        className={!teacher.isVerified ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        {teacher.isVerified ? 'View' : 'Review'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-slate-700">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filteredTeachers.length)} of {filteredTeachers.length}
            </p>
            <div className="flex items-center gap-2">
              <Button
                data-testid="teachers-prev-page"
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-slate-600 dark:text-slate-400 px-2">
                {currentPage} / {totalPages}
              </span>
              <Button
                data-testid="teachers-next-page"
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Teacher Detail Modal with Documents */}
      <Dialog open={!!selectedTeacher} onOpenChange={() => setSelectedTeacher(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              Teacher Details
              {selectedTeacher && !selectedTeacher.isVerified && (
                <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100">
                  Pending Verification
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              Review teacher information and documents for verification
            </DialogDescription>
          </DialogHeader>
          {selectedTeacher && (
            <Tabs defaultValue="info" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="info">Basic Info</TabsTrigger>
                <TabsTrigger value="documents" className="flex items-center gap-2">
                  Documents
                  {!selectedTeacher.isVerified && (
                    <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                  )}
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="info" className="space-y-6 mt-4">
                <div className="flex items-center gap-4">
                  {selectedTeacher.profilePhoto ? (
                    <img 
                      src={selectedTeacher.profilePhoto} 
                      alt={`${selectedTeacher.firstName} ${selectedTeacher.lastName}`}
                      className="w-20 h-20 rounded-full object-cover border-2 border-indigo-200"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400 font-bold text-2xl">
                      {selectedTeacher.firstName?.[0]}{selectedTeacher.lastName?.[0]}
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                      {selectedTeacher.title} {selectedTeacher.firstName} {selectedTeacher.lastName}
                    </h3>
                    <p className="text-slate-500">{selectedTeacher.professionalTitle || 'Teacher'}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        {selectedTeacher.rating?.toFixed(1) || '0.0'} ({selectedTeacher.totalReviews || 0} reviews)
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Email</span>
                    <p className="font-medium text-slate-900 dark:text-white">{selectedTeacher.email}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Phone</span>
                    <p className="font-medium text-slate-900 dark:text-white">{selectedTeacher.phone || 'Not provided'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Price/Session</span>
                    <p className="font-medium text-slate-900 dark:text-white">R{selectedTeacher.pricePerSession}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Experience</span>
                    <p className="font-medium text-slate-900 dark:text-white">{selectedTeacher.experience || 'Not specified'}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-slate-500">Home Address</span>
                    <p className="font-medium text-slate-900 dark:text-white">{selectedTeacher.homeAddress || 'Not provided'}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-slate-500">Subjects</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedTeacher.subjects?.map((s, i) => (
                        <Badge key={i} variant="secondary">{s}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <span className="text-slate-500">Phases/Grades</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedTeacher.phases?.map((p, i) => (
                        <Badge key={i} variant="outline">{p}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <span className="text-slate-500">Bio</span>
                    <p className="font-medium text-slate-900 dark:text-white mt-1">
                      {selectedTeacher.shortBio || 'No bio provided'}
                    </p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="documents" className="space-y-4 mt-4">
                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <h4 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Identity Verification
                  </h4>
                  <DocumentPreview 
                    url={selectedTeacher.saIdProofUrl} 
                    label="SA ID Document" 
                    icon={FileText}
                  />
                </div>

                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <h4 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    Address Verification
                  </h4>
                  <DocumentPreview 
                    url={selectedTeacher.homeAddressProofUrl} 
                    label="Proof of Address" 
                    icon={FileText}
                  />
                </div>

                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <h4 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5" />
                    Academic Qualifications
                  </h4>
                  {selectedTeacher.academicQualifications?.length > 0 ? (
                    <div className="space-y-3">
                      {selectedTeacher.academicQualifications.map((qual, i) => (
                        <div key={i} className="p-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-slate-900 dark:text-white">{qual.qualification}</p>
                              <p className="text-sm text-slate-500">
                                {qual.institution} {qual.year && `(${qual.year})`}
                              </p>
                            </div>
                            {qual.proofUrl && (
                              <a 
                                href={qual.proofUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 px-2 py-1 bg-indigo-50 dark:bg-indigo-900/20 rounded text-indigo-600 dark:text-indigo-400 text-sm hover:bg-indigo-100 dark:hover:bg-indigo-900/40"
                              >
                                <Image className="w-4 h-4" />
                                View Proof
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">No qualifications uploaded</p>
                  )}
                </div>

                {selectedTeacher.profilePhoto && (
                  <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                      <Image className="w-5 h-5" />
                      Profile Photo
                    </h4>
                    <a 
                      href={selectedTeacher.profilePhoto} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <img 
                        src={selectedTeacher.profilePhoto} 
                        alt="Profile" 
                        className="w-32 h-32 rounded-lg object-cover hover:opacity-80 transition-opacity"
                      />
                    </a>
                  </div>
                )}
              </TabsContent>

              <div className="flex gap-3 pt-4 border-t mt-4">
                <Button
                  data-testid="teacher-modal-verify"
                  onClick={() => handleVerify(selectedTeacher.id, selectedTeacher.isVerified)}
                  disabled={actionLoading === selectedTeacher.id}
                  className={selectedTeacher.isVerified 
                    ? "bg-slate-600 hover:bg-slate-700" 
                    : "bg-green-600 hover:bg-green-700"
                  }
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {selectedTeacher.isVerified ? 'Revoke Verification' : 'Verify Teacher'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setSelectedTeacher(null)}
                >
                  Close
                </Button>
              </div>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
